import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { cacheGet, cacheSet } from '../lib/store';
import { classNames, go, TYPE_LABEL, useServerEvents } from '../lib/util';

const TYPES = ['pc', 'npc', 'location', 'faction', 'item', 'quest', 'clue', 'question', 'other'];

export function Records({ campaignId, type }: { campaignId: string; type?: string }) {
  const [list, setList] = useState<any[] | null>(null);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const load = async () => {
    try {
      const l = await api<any[]>(`/api/campaigns/${campaignId}/entities${type ? `?type=${type}` : ''}`);
      setList(l);
      void cacheSet(`records:${campaignId}:${type ?? ''}`, l);
    } catch (e) {
      setList((await cacheGet<any[]>(`records:${campaignId}:${type ?? ''}`)) ?? []);
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    setList(null);
    void load();
  }, [campaignId, type]);
  useServerEvents(campaignId, (what) => what === 'records' && void load());
  const shown = (list ?? []).filter((e) => !filter || `${e.name} ${e.aliases ?? ''}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="page">
      <div className="page-head">
        <h1>Records</h1>
        <button className="btn primary" onClick={() => setCreating(!creating)}>
          New record
        </button>
      </div>
      <nav className="tabs" aria-label="Record types">
        <a href={`#/c/${campaignId}/records`} className={classNames(!type && 'active')}>
          All
        </a>
        {TYPES.map((t) => (
          <a key={t} href={`#/c/${campaignId}/records/${t}`} className={classNames(type === t && 'active')}>
            {TYPE_LABEL[t]}
          </a>
        ))}
      </nav>
      {creating && <NewEntity campaignId={campaignId} defaultType={type ?? 'npc'} onCreated={(e) => go(`/c/${campaignId}/records/e/${e.id}`)} />}
      <input className="filter" placeholder="Filter by name or alias" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter records" />
      {err && <p className="notice">{err}</p>}
      {list === null ? (
        <p className="muted">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="muted">No records{type ? ` of type ${TYPE_LABEL[type]}` : ''} yet.</p>
      ) : (
        <ul className="record-list">
          {shown.map((e) => (
            <li key={e.id}>
              <a href={`#/c/${campaignId}/records/e/${e.id}`} className="record-row">
                <span className={classNames('type', `type-${e.type}`)}>{TYPE_LABEL[e.type]}</span>
                <span className="name">{e.name}</span>
                {e.aliases && <span className="muted small">aka {e.aliases}</span>}
                <span className="spacer" />
                {e.visibility === 'gm_only' && <span className="tag secret">GM only</span>}
                {e.status && !['active', 'open'].includes(e.status) && <span className="tag subtle">{e.status}</span>}
                <span className="muted small">{e.claim_count} claim{e.claim_count === 1 ? '' : 's'}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewEntity({ campaignId, defaultType, onCreated }: { campaignId: string; defaultType: string; onCreated: (e: any) => void }) {
  const [f, setF] = useState({ type: defaultType, name: '', aliases: '', description: '', visibility: '' });
  const [err, setErr] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onCreated(
        await api(`/api/campaigns/${campaignId}/entities`, {
          body: { type: f.type, name: f.name, description: f.description, visibility: f.visibility || undefined, aliases: f.aliases.split(',').map((s) => s.trim()).filter(Boolean) },
        }),
      );
    } catch (x) {
      setErr(errorText(x));
    }
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <label>
        Type
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Name
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required autoFocus />
      </label>
      <label className="span2">
        Aliases (comma separated)
        <input value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} placeholder="e.g. the widow, Mrs H" />
      </label>
      <label className="span2">
        Description
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} />
      </label>
      <label>
        Visibility
        <select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })}>
          <option value="">Campaign default</option>
          <option value="public">Public</option>
          <option value="gm_only">GM only</option>
        </select>
      </label>
      {err && <p className="error span2">{err}</p>}
      <div className="span2">
        <button className="btn primary">Create</button>
      </div>
    </form>
  );
}
